export const ACCESSIBLE_COLORS = {
  black: '#000000',
  white: '#FFFFFF',
  accent: '#2366B1',
  boundary: '#767676',
  secondaryText: '#666666',
  summaryAccent: '#417C8E'
} as const;

type RgbColor = {
  red: number;
  green: number;
  blue: number;
};

function parseHexColor(value: unknown): RgbColor | null {
  if (typeof value !== 'string') return null;

  const match = value.trim().match(/^#?([\da-f]{3}|[\da-f]{6})$/i);
  if (!match) return null;

  const hex = match[1].length === 3 ? [...match[1]].map((character) => character.repeat(2)).join('') : match[1];
  return {
    red: Number.parseInt(hex.slice(0, 2), 16),
    green: Number.parseInt(hex.slice(2, 4), 16),
    blue: Number.parseInt(hex.slice(4, 6), 16)
  };
}

export function normalizeHexColor(value: unknown, fallback?: string): string | null {
  const parsed = parseHexColor(value) || parseHexColor(fallback);
  if (!parsed) return null;

  return `#${[parsed.red, parsed.green, parsed.blue]
    .map((channel) => channel.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
}

function relativeLuminance(color: RgbColor): number {
  const channels = [color.red, color.green, color.blue].map((channel) => {
    const normalized = channel / 255;
    return normalized <= 0.04045 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

export function contrastRatio(first: string, second: string): number {
  const firstColor = parseHexColor(first);
  const secondColor = parseHexColor(second);
  if (!firstColor || !secondColor) return 0;

  const firstLuminance = relativeLuminance(firstColor);
  const secondLuminance = relativeLuminance(secondColor);
  return (Math.max(firstLuminance, secondLuminance) + 0.05) / (Math.min(firstLuminance, secondLuminance) + 0.05);
}

export function resolveBackgroundColor(value: unknown, fallback: string): string {
  return normalizeHexColor(value, fallback) || ACCESSIBLE_COLORS.white;
}

export function resolveTextColor(value: unknown, background: string, fallback: string): string {
  const requestedColor = normalizeHexColor(value, fallback) || ACCESSIBLE_COLORS.black;
  if (contrastRatio(requestedColor, background) >= 4.5) return requestedColor;

  return contrastRatio(ACCESSIBLE_COLORS.black, background) >= contrastRatio(ACCESSIBLE_COLORS.white, background)
    ? ACCESSIBLE_COLORS.black
    : ACCESSIBLE_COLORS.white;
}

export function resolveNonTextColor(
  value: unknown,
  adjacentColor: string,
  fallback = ACCESSIBLE_COLORS.accent
): string {
  const requestedColor = normalizeHexColor(value, fallback) || ACCESSIBLE_COLORS.accent;
  if (contrastRatio(requestedColor, adjacentColor) >= 3) return requestedColor;

  const fallbackColor = normalizeHexColor(fallback, ACCESSIBLE_COLORS.accent) || ACCESSIBLE_COLORS.accent;
  if (contrastRatio(fallbackColor, adjacentColor) >= 3) return fallbackColor;

  return contrastRatio(ACCESSIBLE_COLORS.black, adjacentColor) >= contrastRatio(ACCESSIBLE_COLORS.white, adjacentColor)
    ? ACCESSIBLE_COLORS.black
    : ACCESSIBLE_COLORS.white;
}
